import { agentWritesApi } from '../../api/agents/agent-writes'
import type {
  InFlightAgentActionProposal,
  InFlightTagWritePolicyProposal
} from '../../slices/agent-orchestrator-slice'
import {
  buildTagWritePolicyEntryFromToolInput,
  scheduleHydratePendingAgentWrites
} from '../cypher-write-pending'
import {
  parseSettledProposeOutput,
  readStructuredMutationError,
  registerAgentActionLifecycle,
  type AgentActionApplyOutcome,
  type AgentActionLifecycleCapability,
  type AgentActionRejectOutcome
} from './lifecycle-registry'

export const PROPOSE_TAG_WRITE_POLICY_TOOL_NAME = 'propose_tag_write_policy'

function readAppliedPolicy(result: Record<string, unknown> | undefined): Record<string, unknown> {
  const policy = result?.write_policy ?? result?.writePolicy
  return policy && typeof policy === 'object' && !Array.isArray(policy)
    ? { appliedPolicy: policy as Record<string, unknown> }
    : {}
}

function sanitizeSummary(value: string): string {
  return value.replace(/"/g, '').trim()
}

export const tagWritePolicyLifecycleCapability: AgentActionLifecycleCapability = {
  kind: 'tag_write_policy',
  toolName: PROPOSE_TAG_WRITE_POLICY_TOOL_NAME,

  parseToolStart(toolUseId, input, startedAtMs) {
    const entry = buildTagWritePolicyEntryFromToolInput(toolUseId, input, startedAtMs)
    return entry ? [entry] : []
  },

  parseToolComplete() {
    return null
  },

  parseSettledToolComplete(output) {
    return parseSettledProposeOutput(output)
  },

  scheduleHydratePending(dispatch, conversationId) {
    scheduleHydratePendingAgentWrites(dispatch, conversationId)
  },

  apply(toolUseId) {
    return async (dispatch): Promise<AgentActionApplyOutcome> => {
      try {
        const response = await dispatch(
          agentWritesApi.endpoints.applyCypherWrite.initiate({
            toolUseId,
            graphAffecting: false
          })
        ).unwrap()
        if (response.ok) {
          return { kind: 'success', settledMeta: readAppliedPolicy(response.result) }
        }
        return {
          kind: 'apply_failed',
          error: response.error ?? 'Tag write-policy update failed.'
        }
      } catch (error) {
        const refused = readStructuredMutationError(error)
        if (refused) return { kind: 'apply_failed', error: refused.error }
        return {
          kind: 'transport_error',
          error: error instanceof Error ? error.message : 'Tag write-policy update failed.'
        }
      }
    }
  },

  reject(toolUseId, reason) {
    return async (dispatch): Promise<AgentActionRejectOutcome> => {
      try {
        const response = await dispatch(
          agentWritesApi.endpoints.rejectCypherWrite.initiate({ toolUseId, reason })
        ).unwrap()
        if (response.ok) {
          return {
            kind: 'success',
            settledMeta: { rejectionReason: response.reason ?? reason }
          }
        }
        return { kind: 'reject_failed', error: response.error ?? 'Reject failed.' }
      } catch (error) {
        return {
          kind: 'transport_error',
          error: error instanceof Error ? error.message : 'Reject failed.'
        }
      }
    }
  },

  buildResumeRowLine(proposal: InFlightAgentActionProposal, displayNumber) {
    if (proposal.kind !== 'tag_write_policy') {
      return `${displayNumber}. UNDECIDED — "${sanitizeSummary(proposal.summary)}".`
    }
    const policy = proposal as InFlightTagWritePolicyProposal
    const summary = sanitizeSummary(policy.summary)
    if (policy.terminalState?.kind === 'applied') {
      return `${displayNumber}. APPLIED — "${summary}"; ${String(policy.proposedPolicy.disposition)} policy enabled.`
    }
    if (policy.terminalState?.kind === 'apply_failed') {
      return `${displayNumber}. APPLY_FAILED — "${summary}"; error: ${policy.terminalState.error}.`
    }
    if (policy.terminalState?.kind === 'rejected') {
      return `${displayNumber}. REJECTED — "${summary}"; reason: "${policy.rejectionReason ?? 'rejected by user'}".`
    }
    return `${displayNumber}. UNDECIDED — "${summary}".`
  }
}

registerAgentActionLifecycle(tagWritePolicyLifecycleCapability)
